import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Snackbar,
  CssBaseline,
  Select,
  Option,
  FormLabel,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  Stack,
  Tooltip,
  CircularProgress,
  Breadcrumbs,
} from '@mui/joy';
import {
  Typography as MuiTypography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  KeyboardArrowRight,
} from '@mui/icons-material';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import {
  getUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
} from '../../main/API/authentication';
import PermissionGate from '../../main/components/PermissionGate';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import { PERMISSIONS, useAuth } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { translateAxiosErrorMessage } from '../../main/utils/masterApiError';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { MuiPhone } from '../../main/widgets/MuiPhone';
import 'react-international-phone/build/index.css';
import DeleteAlert from '../../main/widgets/DeleteAlert';

/** True when target is another user (backend rejects self soft-delete). */
function canDeleteUser(targetUserId, currentUserId) {
  if (targetUserId == null || currentUserId == null) return false;
  return Number(targetUserId) !== Number(currentUserId);
}

const USERS_SCREEN_LIST_PERMISSIONS = [
  PERMISSIONS.USERS_LIST,
  PERMISSIONS.USERS_VIEW,
  PERMISSIONS.VIEW_USERS,
];
const USERS_SCREEN_CREATE_PERMISSIONS = [PERMISSIONS.USERS_CREATE, PERMISSIONS.CREATE_USERS];
const USERS_SCREEN_UPDATE_PERMISSIONS = [PERMISSIONS.USERS_UPDATE, PERMISSIONS.EDIT_USERS];
const USERS_SCREEN_DELETE_PERMISSIONS = [PERMISSIONS.USERS_DELETE, PERMISSIONS.DELETE_USERS];

function isValidEmailFormat(value) {
  const s = String(value).trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function validateUserFormFields(formData, editingUser, t) {
  const errors = {};

  if (!String(formData.first_name || '').trim()) {
    errors.first_name = t('settingsPages.users.firstNameRequired');
  }
  if (!String(formData.first_name_ar || '').trim()) {
    errors.first_name_ar = t('settingsPages.users.firstNameArRequired');
  }

  const phone = String(formData.phone_number || '').trim();
  if (!phone || phone === '+968') {
    errors.phone_number = t('settingsPages.users.phoneRequired');
  } else if (phone.replace(/\D/g, '').length < 8) {
    errors.phone_number = t('customerAuth.errInvalidPhone');
  }

  const emailTrimmed = String(formData.email || '').trim();
  if (!emailTrimmed) {
    errors.email = t('customer.emailRequired');
  } else if (!isValidEmailFormat(emailTrimmed)) {
    errors.email = t('customer.emailInvalid');
  }

  if (!editingUser && !String(formData.password || '').trim()) {
    errors.password = t('settingsPages.users.passwordHintNew');
  }

  return errors;
}

export default function UsersScreen() {
  const { t } = useTranslation('ui');
  const { user: currentUser } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const currentUserId = currentUser && currentUser.id != null ? currentUser.id : null;
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    search: '',
    is_active: '',
    is_superuser: '',
  });
  const [openModal, setOpenModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    first_name_ar: '',
    last_name_ar: '',
    id_number: '',
    email: '',
    phone_number: '',
    password: '',
    branch: null,
    is_active: true,
    is_staff: true,
    is_superuser: false,
  });
  
  // Notification states
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  
  // Delete alert states
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const userFormStyles = {
    cardBg: '#ffffff',
    cardShadow: '0 1px 3px rgba(0,0,0,0.06)',
    cardRadius: '16px',
    cardBorder: '1px solid rgba(0,0,0,0.06)',
    titleColor: '#1e293b',
  };

  const loadUsers = useCallback(async () => {
    if (!hasAnyPermission(USERS_SCREEN_LIST_PERMISSIONS)) {
      setUsers([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
        is_staff: true,
      };
      if (filters.search) params.search = filters.search;
      if (filters.is_active !== '') params.is_active = filters.is_active;
      if (filters.is_superuser !== '') params.is_superuser = filters.is_superuser;

      const response = await getUsers(params);
      if (response && response.data) {
        setData(response.data);
        setUsers(response.data.results || []);
      } else {
        setUsers([]);
        setData(null);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      setResponseMessage(error.message || t('settingsPages.users.loadFailed'));
      setFailedNotification(true);
      setUsers([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasAnyPermission, paginationModel.page, paginationModel.pageSize, filters, t]);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleOpenModal = useCallback((user = null) => {
    if (user) {
      setEditingUser(user);
      const activeFromStatus =
        user.status !== undefined ? user.status === 'active' : user.is_active !== undefined ? user.is_active : true;
      setFormData({
        username: user.username || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        first_name_ar: user.first_name_ar ?? '',
        last_name_ar: user.last_name_ar ?? '',
        id_number: user.id_number ?? '',
        email: user.email || '',
        phone_number: user.phone_number || '+968',
        password: '',
        branch: user.branch && user.branch.id ? user.branch.id : null,
        is_active: activeFromStatus,
        is_staff: true,
        is_superuser: user.superuser !== undefined ? Boolean(user.superuser) : Boolean(user.is_superuser),
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        first_name: '',
        last_name: '',
        first_name_ar: '',
        last_name_ar: '',
        id_number: '',
        email: '',
        phone_number: '+968',
        password: '',
        branch: null,
        is_active: true,
        is_staff: true,
        is_superuser: false,
      });
    }
    setFormErrors({});
    setOpenModal(true);
  }, []);

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingUser(null);
    setFormErrors({});
    setFormData(prev => ({ ...prev, password: '' }));
  };

  const phoneHandleChange = (value) => {
    setFormData(prev => ({
      ...prev,
      phone_number: value
    }));
  };

  const handleSave = async () => {
    const requiredSavePermissions = editingUser ? USERS_SCREEN_UPDATE_PERMISSIONS : USERS_SCREEN_CREATE_PERMISSIONS;
    if (!hasAnyPermission(requiredSavePermissions)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    const fieldErrors = validateUserFormFields(formData, editingUser, t);
    if (Object.keys(fieldErrors).length > 0) {
      setFormErrors(fieldErrors);
      return;
    }
    setFormErrors({});

    try {
      setLoading(true);
      const dataToSend = { ...formData, is_staff: true };
      dataToSend.first_name = String(dataToSend.first_name).trim();
      dataToSend.first_name_ar = String(dataToSend.first_name_ar).trim();
      const emailTrimmed = String(dataToSend.email || '').trim();
      dataToSend.email = emailTrimmed;
      if (!dataToSend.last_name_ar || String(dataToSend.last_name_ar).trim() === '') {
        dataToSend.last_name_ar = null;
      } else {
        dataToSend.last_name_ar = String(dataToSend.last_name_ar).trim();
      }
      if (!dataToSend.id_number || String(dataToSend.id_number).trim() === '') {
        dataToSend.id_number = null;
      } else {
        dataToSend.id_number = String(dataToSend.id_number).trim();
      }
      if (editingUser && !dataToSend.password) {
        delete dataToSend.password;
      }
      // Handle empty username - send null instead of empty string
      if (!dataToSend.username || String(dataToSend.username).trim() === '') {
        dataToSend.username = null;
      } else {
        dataToSend.username = String(dataToSend.username).trim();
      }

      if (editingUser) {
        await updateUser(editingUser.id, dataToSend);
        setResponseMessage(t('settingsPages.users.updatedSuccess'));
      } else {
        await createUser(dataToSend);
        setResponseMessage(t('settingsPages.users.createdSuccess'));
      }
      setSuccessNotification(true);
      handleCloseModal();
      loadUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      setResponseMessage(error.message || t('settingsPages.users.saveFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = useCallback((id) => {
    // Prefer blocking the call when the row is the signed-in user
    if (!canDeleteUser(id, currentUserId)) {
      setResponseMessage(t('settingsPages.users.cannotDeleteSelf'));
      setFailedNotification(true);
      return;
    }
    setSelectedUserId(id);
    setDeleteAlert(true);
  }, [currentUserId, t]);

  const confirmDelete = async () => {
    if (!selectedUserId) return;
    if (!canDeleteUser(selectedUserId, currentUserId)) {
      setResponseMessage(t('settingsPages.users.cannotDeleteSelf'));
      setFailedNotification(true);
      setDeleteAlert(false);
      setSelectedUserId(null);
      return;
    }
    if (!hasAnyPermission(USERS_SCREEN_DELETE_PERMISSIONS)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    
    try {
      setDeleteLoading(true);
      await deleteUser(selectedUserId);
      // 204 No Content — soft-delete succeeded for another user
      setResponseMessage(t('settingsPages.users.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteAlert(false);
      setSelectedUserId(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      // Surface backend message (e.g. 400 self-delete) without treating as success
      const apiMessage = translateAxiosErrorMessage(error, t);
      setResponseMessage(apiMessage || t('settingsPages.users.deleteFailed'));
      setFailedNotification(true);
      setDeleteAlert(false);
      setSelectedUserId(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    loadUsers();
  };

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  const columns = useMemo(
    () => [
      { field: 'id', headerName: t('settingsPages.users.colId'), flex: 1, minWidth: 80 },
      {
        field: 'username',
        headerName: t('settingsPages.users.colUsername'),
        flex: 2,
        minWidth: 150,
        renderCell: (params) => {
          const u = params.row.username;
          if (u == null || u === '') return t('actions.na');
          return u;
        },
      },
      {
        field: 'first_name',
        headerName: t('settingsPages.users.colFirstName'),
        flex: 1.5,
        minWidth: 120,
        renderCell: (params) => params.row.first_name || t('actions.na'),
      },
      {
        field: 'last_name',
        headerName: t('settingsPages.users.colLastName'),
        flex: 1.5,
        minWidth: 120,
        renderCell: (params) => params.row.last_name || t('actions.na'),
      },
      {
        field: 'first_name_ar',
        headerName: t('settingsPages.users.colFirstNameAr'),
        flex: 1.5,
        minWidth: 120,
        renderCell: (params) => params.row.first_name_ar || t('actions.na'),
      },
      {
        field: 'last_name_ar',
        headerName: t('settingsPages.users.colLastNameAr'),
        flex: 1.5,
        minWidth: 120,
        renderCell: (params) => params.row.last_name_ar || t('actions.na'),
      },
      {
        field: 'email',
        headerName: t('settingsPages.users.colEmail'),
        flex: 2.5,
        minWidth: 200,
        renderCell: (params) => {
          const e = params.row.email;
          if (e == null || e === '') return t('actions.na');
          return e;
        },
      },
      { field: 'phone_number', headerName: t('settingsPages.users.colPhone'), flex: 2, minWidth: 150 },
      {
        field: 'id_number',
        headerName: t('settingsPages.users.labelIdNumber'),
        flex: 1.5,
        minWidth: 120,
        renderCell: (params) => {
          const v = params.row.id_number;
          if (v == null || v === '') return t('actions.na');
          return v;
        },
      },
      {
        field: 'status',
        headerName: t('settingsPages.permissions.colStatus'),
        flex: 1,
        minWidth: 100,
        renderCell: (params) => {
          const status = params.row.status;
          const isActive =
            status !== undefined
              ? status === 'active'
              : params.row.is_active === true;
          return (
            <Typography
              sx={{
                color: isActive ? 'success.main' : 'neutral.main',
                fontWeight: 500,
              }}
            >
              {status !== undefined
                ? status === 'active'
                  ? t('settingsPages.permissions.statusActive')
                  : status === 'inactive'
                    ? t('settingsPages.permissions.statusInactive')
                    : String(status)
                : params.row.is_active
                  ? t('settingsPages.permissions.statusActive')
                  : t('settingsPages.permissions.statusInactive')}
            </Typography>
          );
        },
      },
      {
        field: 'superuser',
        headerName: t('settingsPages.users.colSuperuser'),
        flex: 1,
        minWidth: 100,
        renderCell: (params) => {
          const v = params.row.superuser !== undefined ? params.row.superuser : params.row.is_superuser;
          return (
            <Typography
              sx={{
                color: v ? 'warning.main' : 'neutral.main',
                fontWeight: 500,
              }}
            >
              {v ? t('settingsPages.users.yes') : t('settingsPages.users.no')}
            </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName: t('settingsPages.users.colActions'),
        flex: 2,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {hasAnyPermission(USERS_SCREEN_UPDATE_PERMISSIONS) && (
              <Tooltip title={t('settingsPages.users.tooltipEdit')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenModal(params.row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {hasAnyPermission(USERS_SCREEN_DELETE_PERMISSIONS) &&
              canDeleteUser(params.row.id, currentUserId) && (
              <Tooltip title={t('settingsPages.users.tooltipDelete')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(params.row.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [t, hasAnyPermission, handleOpenModal, handleDelete, currentUserId]
  );

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit} autoComplete="off">
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width={"100%"}>
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/settings">
                  {t('settingsPages.users.breadcrumbSettings')}
                </Link>
                <Typography level="body-md">{t('settingsPages.users.breadcrumbCurrent')}</Typography>
              </Breadcrumbs>
              <Typography textAlign={"start"} level="h1">{t('settingsPages.users.pageTitle')}</Typography>
            </Box>

            <PermissionGate permissions={USERS_SCREEN_LIST_PERMISSIONS}>
              {/* Filters */}
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Input
                    placeholder={t('settingsPages.users.searchPlaceholder')}
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    startDecorator={<SearchIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Select
                    value={filters.is_active === '' ? 'all' : (filters.is_active ? 'active' : 'inactive')}
                    onChange={(e, value) => setFilters(prev => ({ ...prev, is_active: value === 'all' ? '' : value === 'active' }))}
                  >
                    <Option value="all">{t('settingsPages.permissions.filterAllStatus')}</Option>
                    <Option value="active">{t('settingsPages.permissions.statusActive')}</Option>
                    <Option value="inactive">{t('settingsPages.permissions.statusInactive')}</Option>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Select
                    value={filters.is_superuser === '' ? 'all' : (filters.is_superuser ? 'yes' : 'no')}
                    onChange={(e, value) => setFilters(prev => ({ ...prev, is_superuser: value === 'all' ? '' : value === 'yes' }))}
                  >
                    <Option value="all">{t('settingsPages.users.filterSuperAll')}</Option>
                    <Option value="yes">{t('settingsPages.users.filterSuperYes')}</Option>
                    <Option value="no">{t('settingsPages.users.filterSuperNo')}</Option>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Button type="submit" loading={loading} startDecorator={<SearchIcon />}>
                    {t('actions.search')}
                  </Button>
                </Grid>
              </Grid>

              {/* Actions icons */}
              <Grid mt={2} container spacing={2} justifyContent="flex-end">
                <Grid
                  container
                  spacing={1}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start'
                  }}
                >
                  <PermissionGate permissions={USERS_SCREEN_CREATE_PERMISSIONS}>
                    <Grid item>
                      <Tooltip title={t('settingsPages.users.modalCreateTitle')}>
                        <IconButton
                          onClick={() => handleOpenModal()}
                          color="primary"
                          size="large"
                          sx={{
                            mr: { xs: 0, md: 1 },
                            width: { xs: '100%', md: 'auto' }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </PermissionGate>
                </Grid>
              </Grid>

              {/* Table */}
              <Box>
                <DataGrid
                  rows={users || []}
                  columns={columns}
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  paginationMode="server"
                  rowCount={data ? data.count : 0}
                  onPaginationModelChange={(params) => {
                    if (params.pageSize !== paginationModel.pageSize) {
                      handlePageSizeChange(params.pageSize);
                    } else {
                      handlePageChange(params.page);
                    }
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  hideFooterSelectedRowCount
                  getRowHeight={() => 'auto'}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#333',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      lineHeight: '1.5',
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#555',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      borderBottom: '1px solid #f0f0f0',
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                        '&:hover': {
                          backgroundColor: '#bbdefb',
                        },
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#fafafa',
                      fontSize: '14px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '12px',
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '@media (max-width: 600px)': {
                      '& .MuiDataGrid-toolbarContainer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-footer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-cell': {
                        minHeight: '60px',
                        alignItems: 'center',
                        padding: '8px',
                        fontSize: '13px',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        fontSize: '13px',
                      },
                    }
                  }}
                />
              </Box>
            </PermissionGate>

            {/* Create/Edit Modal */}
            <Modal open={openModal} onClose={handleCloseModal}>
              <ModalDialog
                layout="fullscreen"
                sx={{
                  width: { xs: '100%', sm: '90%', md: '600px', lg: '600px' },
                  maxWidth: '600px',
                  maxHeight: { xs: '100vh', sm: '90vh' },
                  margin: { xs: 0, sm: 'auto' },
                  borderRadius: { xs: 0, sm: '12px' },
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <ModalClose />
                <DialogTitle
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    color: userFormStyles.titleColor,
                    fontWeight: 700,
                    pb: 2,
                    flexShrink: 0,
                  }}
                >
                  {editingUser ? t('settingsPages.users.modalEditTitle') : t('settingsPages.users.modalCreateTitle')}
                </DialogTitle>
                <DialogContent
                  sx={{
                    p: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  <Box
                    sx={{
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      flex: 1,
                      minHeight: 0,
                      maxHeight: { xs: 'calc(100vh - 140px)', sm: 'calc(90vh - 140px)' },
                      px: { xs: 2, sm: 3 },
                      py: 2,
                      WebkitOverflowScrolling: 'touch',
                    }}
                  >
                    <Card
                      variant="plain"
                      sx={{
                        bgcolor: userFormStyles.cardBg,
                        boxShadow: userFormStyles.cardShadow,
                        borderRadius: userFormStyles.cardRadius,
                        border: userFormStyles.cardBorder,
                        overflow: 'hidden',
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                        <Stack spacing={2}>
                          <FormLabel>{t('settingsPages.users.labelUsername')}</FormLabel>
                          <Input
                            value={formData.username}
                            onChange={(e) => {
                              setFormData(prev => ({ ...prev, username: e.target.value }));
                              setFormErrors((prev) => {
                                const next = { ...prev };
                                delete next.username;
                                return next;
                              });
                            }}
                            placeholder={t('settingsPages.users.labelUsername')}
                            autoComplete="off"
                          />

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <FormLabel>{`${t('settingsPages.users.labelFirstName')} *`}</FormLabel>
                              <Input
                                value={formData.first_name}
                                onChange={(e) => {
                                  setFormData(prev => ({ ...prev, first_name: e.target.value }));
                                  setFormErrors((prev) => {
                                    const next = { ...prev };
                                    delete next.first_name;
                                    return next;
                                  });
                                }}
                                placeholder={t('settingsPages.users.labelFirstName')}
                                required
                                slotProps={{ input: { minLength: 1, maxLength: 30 } }}
                              />
                              {formErrors.first_name && (
                                <MuiTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                  {formErrors.first_name}
                                </MuiTypography>
                              )}
                            </Grid>
                            <Grid item xs={6}>
                              <FormLabel>{t('settingsPages.users.labelLastName')}</FormLabel>
                              <Input
                                value={formData.last_name}
                                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                                placeholder={t('settingsPages.users.labelLastName')}
                              />
                            </Grid>
                          </Grid>

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <FormLabel>{`${t('settingsPages.users.labelFirstNameAr')} *`}</FormLabel>
                              <Input
                                value={formData.first_name_ar}
                                onChange={(e) => {
                                  setFormData(prev => ({ ...prev, first_name_ar: e.target.value }));
                                  setFormErrors((prev) => {
                                    const next = { ...prev };
                                    delete next.first_name_ar;
                                    return next;
                                  });
                                }}
                                placeholder={t('settingsPages.users.labelFirstNameAr')}
                                required
                                slotProps={{ input: { minLength: 1, maxLength: 30 } }}
                              />
                              {formErrors.first_name_ar && (
                                <MuiTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                  {formErrors.first_name_ar}
                                </MuiTypography>
                              )}
                            </Grid>
                            <Grid item xs={6}>
                              <FormLabel>{`${t('settingsPages.users.labelLastNameAr')} (${t('actions.optional')})`}</FormLabel>
                              <Input
                                value={formData.last_name_ar}
                                onChange={(e) => setFormData(prev => ({ ...prev, last_name_ar: e.target.value }))}
                                placeholder={t('settingsPages.users.labelLastNameAr')}
                                slotProps={{ input: { maxLength: 30 } }}
                              />
                            </Grid>
                          </Grid>

                          <FormLabel>{`${t('settingsPages.users.labelIdNumber')} (${t('actions.optional')})`}</FormLabel>
                          <Input
                            value={formData.id_number}
                            onChange={(e) => setFormData(prev => ({ ...prev, id_number: e.target.value }))}
                            placeholder={t('settingsPages.users.labelIdNumber')}
                            autoComplete="off"
                          />

                          <FormLabel>{`${t('settingsPages.users.labelEmail')} *`}</FormLabel>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => {
                              setFormData(prev => ({ ...prev, email: e.target.value }));
                              setFormErrors((prev) => {
                                const next = { ...prev };
                                delete next.email;
                                return next;
                              });
                            }}
                            placeholder={t('settingsPages.users.emailPlaceholder')}
                            required
                          />
                          {formErrors.email && (
                            <MuiTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              {formErrors.email}
                            </MuiTypography>
                          )}

                          <FormLabel>{`${t('settingsPages.users.labelPhone')} *`}</FormLabel>
                          <Box>
                            <MuiPhone
                              value={formData.phone_number || '+968'}
                              onChange={(value) => {
                                phoneHandleChange(value);
                                setFormErrors((prev) => {
                                  const next = { ...prev };
                                  delete next.phone_number;
                                  return next;
                                });
                              }}
                            />
                            {formErrors.phone_number && (
                              <MuiTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                {formErrors.phone_number}
                              </MuiTypography>
                            )}
                          </Box>

                          <FormLabel>
                            {editingUser ? t('settingsPages.users.passwordHintEdit') : `${t('settingsPages.users.labelPassword')} *`}
                          </FormLabel>
                          <Input
                            type="password"
                            value={formData.password}
                            onChange={(e) => {
                              setFormData(prev => ({ ...prev, password: e.target.value }));
                              setFormErrors((prev) => {
                                const next = { ...prev };
                                delete next.password;
                                return next;
                              });
                            }}
                            placeholder={t('settingsPages.users.labelPassword')}
                            autoComplete="new-password"
                          />
                          {formErrors.password && (
                            <MuiTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              {formErrors.password}
                            </MuiTypography>
                          )}

                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                            <NativeCheckbox
                              size="sm"
                              checked={formData.is_active}
                              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                              label={t('settingsPages.permissions.labelActive')}
                            />
                            <NativeCheckbox
                              size="sm"
                              checked={formData.is_superuser}
                              onChange={(e) => setFormData(prev => ({ ...prev, is_superuser: e.target.checked }))}
                              label={t('settingsPages.users.labelSuperuser')}
                            />
                          </Box>
                        </Stack>

                        <Box
                          sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'flex-end',
                            flexWrap: 'wrap',
                            pt: 2,
                            mt: 2,
                            borderTop: '1px solid rgba(0,0,0,0.06)',
                          }}
                        >
                          <Button variant="plain" color="neutral" onClick={handleCloseModal}>
                            {t('actions.cancel')}
                          </Button>
                          <PermissionGate permissions={editingUser ? USERS_SCREEN_UPDATE_PERMISSIONS : USERS_SCREEN_CREATE_PERMISSIONS}>
                            <Button
                              onClick={handleSave}
                              disabled={loading}
                              startDecorator={loading ? <CircularProgress size="sm" /> : <SaveIcon />}
                            >
                              {loading ? t('settingsPages.common.saving') : t('actions.save')}
                            </Button>
                          </PermissionGate>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                </DialogContent>
              </ModalDialog>
            </Modal>

            {/* Notifications */}
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={() => setSuccessNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={() => setFailedNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            <DeleteAlert
              label={t('actions.user')}
              open={deleteAlert}
              onDelete={confirmDelete}
              onClose={() => {
                setDeleteAlert(false);
                setSelectedUserId(null);
              }}
              loading={deleteLoading}
            />
          </Box>
        </Box>
      </form>
    </DirectionalMaterialThemeProvider>
  );
}

